import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Brain, MessageCircleQuestion, Sparkles, Trash2, Edit3, Plus, TrendingUp, Search, X, BookOpen, RefreshCw, Save } from "lucide-react";

export default function AdminAIKnowledge() {
  const [tab, setTab] = useState("overview"); // overview | gaps | kb
  const [stats, setStats] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [entries, setEntries] = useState([]);
  const [overview, setOverview] = useState(null);
  const [q, setQ] = useState("");
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const loadStats = () => api.get("/admin/ai-knowledge/stats").then((r) => setStats(r.data));
  const loadGaps = () => api.get("/admin/ai-knowledge/gaps", { params: q ? { q } : {} }).then((r) => setGaps(r.data));
  const loadKB = () => api.get("/admin/ai-knowledge/entries", { params: q ? { q } : {} }).then((r) => setEntries(r.data));
  const loadOverview = () => api.get("/admin/ai-knowledge/overview").then((r) => setOverview(r.data));

  useEffect(() => { loadStats(); loadOverview(); }, []);
  useEffect(() => {
    if (tab === "gaps") loadGaps();
    else if (tab === "kb") loadKB();
    else if (tab === "overview") loadOverview();
    // eslint-disable-next-line
  }, [tab, q]);

  const seedCommon = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/ai-knowledge/seed-common");
      toast.success(`تمت إضافة ${data.created} سؤال جديد`);
      loadOverview(); loadStats();
      setTab("overview");
    } catch { toast.error("فشل الحفظ"); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-ai-knowledge">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-semibold flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-600" /> ذكاء المدرب
          </h1>
          <p className="text-foreground/60 text-sm mt-1">
            الذكاء يحفظ الأسئلة اللي محتاج جوابك عليها — بمجرد ما تجيب، يستخدم جوابك مع أي مستخدم بعدك.
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
          <StatCard label="أسئلة تحتاج جوابك" value={stats.pending_gaps} accent="bg-amber-100 text-amber-800 border-amber-300" testid="stat-pending" />
          <StatCard label="أسئلة أجبتها" value={stats.answered_gaps} accent="bg-emerald-100 text-emerald-800 border-emerald-300" testid="stat-answered" />
          <StatCard label="قاعدة المعرفة" value={stats.active_entries} accent="bg-purple-100 text-purple-800 border-purple-300" testid="stat-kb" />
          <StatCard label="استخدامات مجمّعة" value={(stats.top_used || []).reduce((s, e) => s + (e.times_used || 0), 0)} accent="bg-blue-100 text-blue-800 border-blue-300" testid="stat-uses" />
        </div>
      )}

      {/* Tab switch */}
      <div className="flex gap-2 border-b border-foreground/10 overflow-x-auto no-scrollbar">
        <button
          onClick={() => setTab("overview")}
          className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "overview" ? "border-primary text-primary" : "border-transparent text-foreground/60"}`}
          data-testid="tab-overview"
        >
          <Brain className="h-4 w-4 inline-block me-1" />
          ما يعرفه الذكاء
        </button>
        <button
          onClick={() => setTab("gaps")}
          className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "gaps" ? "border-primary text-primary" : "border-transparent text-foreground/60"}`}
          data-testid="tab-gaps"
        >
          <MessageCircleQuestion className="h-4 w-4 inline-block me-1" />
          أسئلة تحتاج جواب {stats?.pending_gaps ? `(${stats.pending_gaps})` : ""}
        </button>
        <button
          onClick={() => setTab("kb")}
          className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "kb" ? "border-primary text-primary" : "border-transparent text-foreground/60"}`}
          data-testid="tab-kb"
        >
          <Sparkles className="h-4 w-4 inline-block me-1" />
          قاعدة المعرفة {stats?.active_entries ? `(${stats.active_entries})` : ""}
        </button>
      </div>

      {/* Overview tab */}
      {tab === "overview" && overview && (
        <OverviewPanel
          overview={overview}
          seeding={seeding}
          onSeed={seedCommon}
          onEditEntry={(id) => { setTab("kb"); setEditingId(id); }}
          onReload={() => { loadOverview(); loadStats(); }}
        />
      )}

      {/* Search + new entry action (only for gaps/kb tabs) */}
      {tab !== "overview" && (
        <div className="flex gap-2 items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute top-2.5 start-2.5 h-4 w-4 text-foreground/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث..." className="ps-9" data-testid="kb-search" />
          </div>
          {tab === "kb" && (
            <Button size="sm" onClick={() => setShowNewEntry(true)} className="gap-1" data-testid="btn-new-entry">
              <Plus className="h-4 w-4" /> إضافة يدوية
            </Button>
          )}
        </div>
      )}

      {/* Gaps list */}
      {tab === "gaps" && (
        <div className="space-y-3">
          {gaps.length === 0 ? (
            <Card className="p-8 text-center text-foreground/60 border-dashed">
              🎉 لا أسئلة معلقة — الذكاء واثق من كل شيء حالياً.
            </Card>
          ) : gaps.map((g) => (
            <GapCard key={g.id} g={g} onAnswered={() => { loadGaps(); loadStats(); }} />
          ))}
        </div>
      )}

      {/* KB list */}
      {tab === "kb" && (
        <div className="space-y-3">
          {showNewEntry && (
            <ManualEntryCard
              onSaved={() => { setShowNewEntry(false); loadKB(); loadStats(); }}
              onCancel={() => setShowNewEntry(false)}
            />
          )}
          {entries.length === 0 && !showNewEntry ? (
            <Card className="p-8 text-center text-foreground/60 border-dashed">
              قاعدة المعرفة فارغة. أجب على الأسئلة المعلقة أو أضف بنداً يدوياً.
            </Card>
          ) : entries.map((e) => (
            <EntryCard
              key={e.id}
              e={e}
              editing={editingId === e.id}
              onEdit={() => setEditingId(e.id)}
              onCancelEdit={() => setEditingId(null)}
              onSaved={() => { setEditingId(null); loadKB(); }}
              onDeleted={() => { loadKB(); loadStats(); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewPanel({ overview, seeding, onSeed, onEditEntry, onReload }) {
  const meta = overview?.categories_meta || {};
  const byCat = overview?.qa_by_category || {};
  const courses = overview?.courses || [];
  const catOrder = ["subscription", "courses", "nutrition", "general", "custom"];
  const orderedCats = [
    ...catOrder.filter((c) => byCat[c]?.length),
    ...Object.keys(byCat).filter((c) => !catOrder.includes(c)),
  ];

  return (
    <div className="space-y-6" data-testid="overview-panel">
      {/* Seed helper — always visible, idempotent */}
      <Card className="p-5 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 grid place-items-center shrink-0">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-semibold text-base">ابدأ بحقن الأسئلة الشائعة</div>
            <p className="text-sm text-foreground/60 mt-1 leading-relaxed">
              أضف 13 سؤال وجواب جاهز (اشتراك، دورات، تغذية، عام) بضغطة واحدة. آمن للتكرار — لن يُضاف سؤال موجود مسبقاً. يمكنك تعديل أي جواب بعد الحقن.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={onSeed} disabled={seeding} className="gap-1" data-testid="btn-seed-common">
              <Sparkles className="h-4 w-4" /> {seeding ? "جاري..." : "حقن الأسئلة الشائعة"}
            </Button>
            <Button variant="outline" onClick={onReload} disabled={seeding} data-testid="btn-reload-overview" aria-label="تحديث">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Live course summary */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded bg-blue-100 text-blue-700 grid place-items-center">🎓</div>
          <h2 className="font-display font-semibold text-lg">دورات يعرفها الذكاء</h2>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">{courses.length}</Badge>
        </div>
        {courses.length === 0 ? (
          <Card className="p-6 text-center text-foreground/50 border-dashed text-sm">
            لا توجد دورات منشورة حالياً. الذكاء يعتمد على المحتوى المنشور فقط.
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {courses.map((c, i) => (
              <Card key={i} className="p-4 border-foreground/10" data-testid={`overview-course-${i}`}>
                <div className="font-semibold text-sm leading-relaxed">{c.title}</div>
                {c.description && (
                  <div className="text-xs text-foreground/60 mt-1 line-clamp-2">{c.description}</div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
                  <Badge variant="outline" className="bg-foreground/5">
                    {c.sections_count} قسم · {c.lesson_count} درس
                  </Badge>
                  {c.level && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{c.level}</Badge>}
                  {c.category && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{c.category}</Badge>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Q&A grouped by category */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded bg-purple-100 text-purple-700 grid place-items-center">💡</div>
          <h2 className="font-display font-semibold text-lg">ما يعرفه الذكاء (قاعدة المعرفة)</h2>
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">{overview?.total_entries || 0}</Badge>
        </div>

        {orderedCats.length === 0 ? (
          <Card className="p-8 text-center text-foreground/60 border-dashed text-sm">
            قاعدة المعرفة فارغة — اضغط "حقن الأسئلة الشائعة" أعلاه للبدء.
          </Card>
        ) : (
          <div className="space-y-4">
            {orderedCats.map((cat) => {
              const info = meta[cat] || { label: cat, icon: "✨" };
              const items = byCat[cat] || [];
              return (
                <Card key={cat} className="p-4 border-foreground/10" data-testid={`overview-cat-${cat}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-display font-semibold text-sm flex items-center gap-2">
                      <span className="text-base">{info.icon}</span>
                      {info.label}
                    </div>
                    <Badge variant="outline" className="text-xs">{items.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {items.map((it) => (
                      <button
                        key={it.id}
                        onClick={() => onEditEntry(it.id)}
                        className="w-full text-start p-3 rounded-lg hover:bg-foreground/5 border border-transparent hover:border-foreground/10 transition group"
                        data-testid={`overview-item-${it.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-medium leading-relaxed flex-1">س: {it.question}</div>
                          <Edit3 className="h-3.5 w-3.5 text-foreground/30 group-hover:text-primary shrink-0 mt-0.5" />
                        </div>
                        <div className="text-xs text-foreground/60 mt-1 line-clamp-2 leading-relaxed">ج: {it.answer}</div>
                        {(it.times_used || 0) > 0 && (
                          <div className="text-[11px] text-foreground/40 mt-1 flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" /> استُخدم {it.times_used} مرة
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <Card className={`p-4 border ${accent}`} data-testid={testid}>
      <div className="text-2xl font-display font-bold">{value ?? 0}</div>
      <div className="text-xs mt-0.5">{label}</div>
    </Card>
  );
}

function GapCard({ g, onAnswered }) {
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (answer.trim().length < 5) { toast.error("الجواب قصير جداً"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/ai-knowledge/gaps/${g.id}/answer`, { answer: answer.trim() });
      toast.success("تم حفظ الجواب في قاعدة المعرفة — الذكاء سيستخدمه من الآن.");
      onAnswered();
    } catch (e) {
      toast.error("فشل الحفظ");
    } finally { setSaving(false); }
  };

  const dismiss = async () => {
    if (!window.confirm("تجاهل هذا السؤال بدون جواب؟")) return;
    await api.post(`/admin/ai-knowledge/gaps/${g.id}/dismiss`);
    toast.success("تم التجاهل");
    onAnswered();
  };

  return (
    <Card className="p-5 border-foreground/10" data-testid={`gap-${g.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 grid place-items-center shrink-0">
          <Brain className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-foreground/50 mb-1">
            الذكاء طلب توضيحاً · {new Date(g.created_at).toLocaleString("ar-EG")}
          </div>
          <div className="font-semibold text-sm leading-relaxed">{g.question}</div>
        </div>
        <Badge variant="outline" className="shrink-0 bg-amber-50 text-amber-800 border-amber-300">قيد الإجابة</Badge>
      </div>

      {g.assistant_reply && (
        <details className="mb-3 text-xs">
          <summary className="cursor-pointer text-foreground/50 hover:text-foreground">عرض الجواب المبدئي من الذكاء</summary>
          <div className="mt-2 p-2 bg-foreground/5 rounded text-foreground/70 whitespace-pre-wrap max-h-40 overflow-y-auto">
            {g.assistant_reply}
          </div>
        </details>
      )}

      <label className="text-xs font-semibold text-foreground/70 mb-1 block">جوابك الرسمي:</label>
      <Textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={4}
        placeholder="اكتب جوابك هنا... سيستخدمه الذكاء ككلمة نهائية عند أي مستخدم يسأل نفس الشيء."
        data-testid={`gap-answer-${g.id}`}
      />

      <div className="flex gap-2 mt-3">
        <Button onClick={submit} disabled={saving} className="gap-1" data-testid={`btn-answer-${g.id}`}>
          <Sparkles className="h-4 w-4" /> {saving ? "جاري الحفظ..." : "احفظ في قاعدة المعرفة"}
        </Button>
        <Button variant="outline" onClick={dismiss} disabled={saving} data-testid={`btn-dismiss-${g.id}`}>
          تجاهل
        </Button>
      </div>
    </Card>
  );
}

function ManualEntryCard({ onSaved, onCancel }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [keywordsExtra, setKeywordsExtra] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (question.trim().length < 5 || answer.trim().length < 5) { toast.error("املأ السؤال والجواب"); return; }
    setSaving(true);
    try {
      await api.post("/admin/ai-knowledge/entries", { question, answer, keywords_extra: keywordsExtra });
      toast.success("تمت الإضافة");
      onSaved();
    } catch (e) { toast.error("فشل"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5 border-2 border-purple-300 bg-purple-50/40" data-testid="manual-entry-card">
      <div className="font-display font-semibold mb-3 text-sm">➕ إضافة يدوية</div>
      <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="السؤال..." className="mb-2" data-testid="manual-question" />
      <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4} placeholder="الجواب..." className="mb-2" data-testid="manual-answer" />
      <Input value={keywordsExtra} onChange={(e) => setKeywordsExtra(e.target.value)} placeholder="كلمات مفتاحية إضافية (اختياري، افصل بمسافات)" className="mb-3" data-testid="manual-keywords" />
      <div className="flex gap-2">
        <Button onClick={submit} disabled={saving} className="gap-1"><Sparkles className="h-4 w-4" /> حفظ</Button>
        <Button variant="outline" onClick={onCancel} disabled={saving}>إلغاء</Button>
      </div>
    </Card>
  );
}

function EntryCard({ e, editing, onEdit, onCancelEdit, onSaved, onDeleted }) {
  const [q, setQ] = useState(e.question);
  const [a, setA] = useState(e.answer);

  const save = async () => {
    await api.patch(`/admin/ai-knowledge/entries/${e.id}`, { question: q, answer: a });
    toast.success("تم التحديث");
    onSaved();
  };
  const del = async () => {
    if (!window.confirm("حذف هذا البند نهائياً؟")) return;
    await api.delete(`/admin/ai-knowledge/entries/${e.id}`);
    toast.success("تم الحذف");
    onDeleted();
  };

  return (
    <Card className="p-5 border-foreground/10" data-testid={`kb-entry-${e.id}`}>
      {editing ? (
        <>
          <Input value={q} onChange={(ev) => setQ(ev.target.value)} className="mb-2" />
          <Textarea value={a} onChange={(ev) => setA(ev.target.value)} rows={4} className="mb-3" />
          <div className="flex gap-2">
            <Button size="sm" onClick={save}>حفظ</Button>
            <Button size="sm" variant="outline" onClick={onCancelEdit}>إلغاء</Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-relaxed">س: {e.question}</div>
              <div className="text-sm text-foreground/70 mt-2 leading-relaxed whitespace-pre-wrap">ج: {e.answer}</div>
              <div className="flex items-center gap-2 mt-3 text-[11px] text-foreground/50">
                <TrendingUp className="h-3 w-3" /> استُخدم <b className="text-foreground/70">{e.times_used || 0}</b> مرة
                <span>·</span>
                <span>{(e.keywords || []).slice(0, 6).join(" · ")}</span>
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button onClick={onEdit} className="p-1.5 rounded hover:bg-foreground/5" data-testid={`btn-edit-${e.id}`} aria-label="تعديل">
                <Edit3 className="h-3.5 w-3.5" />
              </button>
              <button onClick={del} className="p-1.5 rounded hover:bg-rose-100 text-rose-600" data-testid={`btn-delete-${e.id}`} aria-label="حذف">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
